"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertTriangle, Check as CheckCircle2, CircleQuestionMark as HelpCircle, Phone, Wrench, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const faqItems = [{
  id: "faq-1",
  question: "Why is my garbage disposal humming but not spinning?",
  answer: "A humming sound usually means the motor has power but the grinding flywheel is physically jammed by a hard food scrap, bone, or foreign object. Turn off the wall switch immediately to avoid burning out the motor or tripping your circuit breaker. Never put your hand inside the chamber. If the reset button underneath does not resolve it after clearing power, a plumber can safely free the flywheel and inspect the internal blades.",
  tip: "Turn off the wall switch right away to prevent motor burnout."
}, {
  id: "faq-2",
  question: "What causes water to leak from the bottom of the disposal unit?",
  answer: "Leaks beneath the disposal typically stem from degraded internal seals, cracked housing, or loose mounting flange gaskets. While loose drain pipe slip joints can easily be tightened, water dripping directly from the bottom reset button or electrical housing means the internal motor seal has failed. In most cases, a unit leaking from the base requires replacement rather than repair.",
  tip: "Leaks directly from the bottom shell generally require unit replacement."
}, {
  id: "faq-3",
  question: "How do I know if I should repair or replace my garbage disposal?",
  answer: "Most garbage disposals have a lifespan of 8 to 12 years. If your unit is under 5 years old and experiencing a straightforward jam, broken switch, or external leak, a repair is cost-effective. However, if the motor has burned out, the housing is rusted through, or repairs exceed 50% of a new installation cost, upgrading to a high-torque, stainless steel model is the smarter long-term choice.",
  tip: "Units over 10 years old with motor failure are best replaced."
}, {
  id: "faq-4",
  question: "Why does my garbage disposal trip the reset button or breaker repeatedly?",
  answer: "The red overload protector button trips when the motor draws excessive electrical current. This happens due to mechanical jams, heavy loads of fibrous food (like celery or corn husks), worn internal bearings, or an electrical short circuit inside the motor windings. If it trips immediately after resetting without food inside, professional diagnosis is required.",
  tip: "Wait 5 minutes before pressing the red reset button under the unit."
}, {
  id: "faq-5",
  question: "Can I use chemical drain cleaners if my disposal is clogged?",
  answer: "No. Never pour caustic liquid chemical drain cleaners down a garbage disposal. Harsh chemicals can corrode the internal rubber seals, destroy the aluminum or plastic casing, and splash dangerous acid onto you when the disposal spins. If backed up water is present, turn off the disposal and call a licensed plumber to clear the trap and drain line mechanically.",
  tip: "Chemical cleaners deteriorate internal gaskets and void manufacturer warranties."
}];
export default function DisposalFaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="garbagedisposalfaq" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 inline-flex items-center gap-1.5 px-3 py-1 font-medium text-xs tracking-wide uppercase">
            <HelpCircle className="size-3.5 text-accent" />
            Troubleshooting Guide
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Garbage Disposal Repair FAQ
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Quick answers to common sink disposal issues in Jonesboro, GA. Learn what causes jams, leaks, and electrical trips before scheduling your emergency repair.
          </p>
        </div>

        {/* FAQ & Support Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Accordion Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="lg:col-span-8">
            <Accordion type="single" collapsible defaultValue="faq-1" className="w-full space-y-4">
              {faqItems.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="bg-card text-card-foreground border border-border rounded-xl px-5 sm:px-6 shadow-xs transition-all hover:border-primary/40 data-[state=open]:border-primary/60 data-[state=open]:shadow-sm">
                  <AccordionTrigger className="text-base sm:text-lg font-default font-semibold py-5 hover:no-underline text-foreground text-left gap-4">
                    <span className="flex items-start gap-3">
                      <span className="flex items-center justify-center size-6 rounded-md bg-secondary text-secondary-foreground text-xs font-mono font-bold mt-0.5 shrink-0">
                        0{index + 1}
                      </span>
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pt-1 pb-6 text-sm sm:text-base border-t border-border/60 mt-1">
                    <p className="mb-4">{item.answer}</p>
                    <div className="flex items-start gap-2.5 rounded-lg bg-secondary/60 p-3 text-xs sm:text-sm text-secondary-foreground font-medium border border-border/40">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>
                        <strong className="font-semibold text-foreground">Pro Tip: </strong>
                        {item.tip}
                      </span>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>

          {/* Side Support Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.15
        }} className="lg:col-span-4 lg:sticky lg:top-24">
            <Card className="border border-border bg-card shadow-md">
              <CardContent className="p-6 sm:p-8 flex flex-col gap-6">
                <div className="flex items-center justify-center size-12 rounded-xl bg-primary text-primary-foreground">
                  <Wrench className="size-6" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl font-default font-bold text-card-foreground">
                    Disposal Still Not Working?
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Don&apos;t risk kitchen sink flooding or electrical hazards. Jonesboro Emergency Plumber provides 24/7 same-day diagnosis, clearing, and disposal replacement.
                  </p>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-muted-foreground">
                    <Zap className="size-4 text-accent shrink-0" />
                    <span>Fast 24/7 response across Jonesboro, GA</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-muted-foreground">
                    <AlertTriangle className="size-4 text-accent shrink-0" />
                    <span>Upfront repair vs. replace recommendations</span>
                  </div>
                </div>

                <div className="flex flex-col gap-3 pt-2">
                  <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-sm">
                    <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                      <Phone className="size-4" />
                      <span>Request Emergency Service</span>
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="default" className="w-full font-medium">
                    <Link to="/services/">
                      View All Plumbing Services
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}